import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, DefaultButton, Pivot, PivotItem, MessageBar, MessageBarType, CommandBar } from '@fluentui/react';
import styles from './LocalRun.module.css';

type Platform = 'macos' | 'windows' | 'linux';

const PLATFORM_LABELS: Record<Platform, string> = {
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

function buildDockerCmd(platform: Platform, ghToken: string): string {
  const sep = platform === 'windows' ? '`' : '\\';
  return [
    `docker run -it ${sep}`,
    `  -e GH_TOKEN="${ghToken || '<your-token>'}" ${sep}`,
    `  -p 8080:8080 ${sep}`,
    `  -p 2222:2222 ${sep}`,
    `  ghcr.io/networg/agentbox:latest`,
  ].join('\n');
}

export default function LocalRun() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState<Platform>('macos');
  const [copied, setCopied] = useState(false);

  const ghToken = sessionStorage.getItem('agentbox:github-token') ?? '';
  const cmd = buildDockerCmd(platform, ghToken);

  function handleCopy() {
    navigator.clipboard.writeText(cmd).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  return (
    <div className={styles.page}>
      <CommandBar
        items={[{ key: 'back', iconOnly: true, iconProps: { iconName: 'Back' }, onClick: () => { navigate('/'); }, ariaLabel: 'Back' }]}
        styles={{ root: { borderBottom: '1px solid var(--ab-border)', background: 'var(--ab-bg-command-bar)', padding: '0 8px' } }}
      />
      <div className={styles.formHeader}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Local Run</Text>
      </div>
      <div className={styles.body}>
        <Text variant="medium" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
          Run an AgentBox locally with Docker. Tokens are pre-filled from your session.
        </Text>

        <Pivot onLinkClick={(item) => { if (item?.props.itemKey) setPlatform(item.props.itemKey as Platform); }} selectedKey={platform}>
          {(Object.keys(PLATFORM_LABELS) as Platform[]).map((p) => (
            <PivotItem key={p} itemKey={p} headerText={PLATFORM_LABELS[p]} />
          ))}
        </Pivot>

        <div className={styles.cmdBlock}>
          <pre className={styles.code}>{cmd}</pre>
          <DefaultButton text={copied ? '✓ Copied' : 'Copy'} iconProps={{ iconName: 'Copy' }} onClick={handleCopy} styles={{ root: { alignSelf: 'flex-start' } }} />
        </div>

        {!ghToken && (
          <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
            Connect GitHub in the spawn form first to auto-fill your token.
          </MessageBar>
        )}

        <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
          Open <code>http://localhost:8080</code> after running.
        </MessageBar>
      </div>
    </div>
  );
}
